import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Card } from '@/components/ui/card'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useIsReadOnly } from '@/lib/casl'
import { useCustomPages } from '@/lib/services/custom-pages/custom-pages.query'
import type { CustomPageSummary } from '@/lib/services/custom-pages/custom-pages.types'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Files } from 'lucide-react'
import {
  CUSTOM_PAGES_DESCRIPTION,
  CustomPageAddress,
  CustomPageMenuNotice,
  CustomPageStatusBadge,
  formatCustomPageUpdatedAt,
} from './-custom-page-parts'
import { loadCustomPagesRoute } from './-loader'

export const Route = createFileRoute('/_authenticated/$orgId/website/custom-pages/')({
  component: CustomPagesPage,
  loader: loadCustomPagesRoute,
})

const customPageColumns: ColumnDef<CustomPageSummary>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    meta: { label: 'Title', kind: 'primary', cellClassName: 'font-medium' },
  },
  {
    accessorKey: 'path',
    header: 'Address',
    cell: ({ row }) => <CustomPageAddress path={row.original.path} />,
    // The copy button sits in this cell, so it stays out of the row link.
    meta: { label: 'Address', kind: 'text', link: false },
  },
  {
    accessorKey: 'is_published',
    header: 'Status',
    cell: ({ row }) => <CustomPageStatusBadge isPublished={row.original.is_published} />,
    meta: { label: 'Status', kind: 'badge' },
  },
  {
    accessorKey: 'updated_at',
    header: 'Last updated',
    cell: ({ row }) => formatCustomPageUpdatedAt(row.original.updated_at),
    meta: { label: 'Last updated', kind: 'date', cellClassName: 'whitespace-nowrap tabular-nums' },
  },
]

function OrganizationCustomPages() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const { data: customPages } = useCustomPages({ organizationId: orgId })
  const { isReadOnly } = useIsReadOnly()

  return (
    <AdminPageShell maxWidth="content" className="space-y-4">
      <AdminPageHeader
        icon={Files}
        title="Custom Pages"
        description={CUSTOM_PAGES_DESCRIPTION}
        actions={
          <Can I="create" a="custom-pages">
            <CreateActionButton asChild>
              <Link {...path.customPages.create().using({ orgId }).link}>New page</Link>
            </CreateActionButton>
          </Can>
        }
      />

      {isReadOnly && customPages.some((customPage) => customPage.is_published) ? (
        <CustomPageMenuNotice
          title="Add a page to your menu"
          changeText="To change a page or request a new one, contact our support team."
        />
      ) : null}

      {customPages.length ? (
        <Card className="overflow-hidden py-0">
          <IndexDataTable
            mode="client"
            data={customPages}
            columns={customPageColumns}
            getRowId={(customPage) => String(customPage.id)}
            rowLink={(customPage) => path.customPages.show(customPage.id).using({ orgId }).link}
            pagination={false}
          />
        </Card>
      ) : (
        <EmptyState
          icon={Files}
          title="No custom pages yet"
          description={
            isReadOnly
              ? 'Contact our support team to request a page for events, classes, policies or anything else your shoppers ask about.'
              : 'Create a page to add events, classes, policies or other content to this website.'
          }
        />
      )}
    </AdminPageShell>
  )
}

export function CustomPagesPage() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <AdminPageShell maxWidth="content" className="space-y-4">
          <AdminPageHeader
            icon={Files}
            title="Custom Pages"
            description={CUSTOM_PAGES_DESCRIPTION}
          />
          <OrganizationContextNotice>
            Choose a florist account to manage its custom pages.
          </OrganizationContextNotice>
        </AdminPageShell>
      }
    >
      <OrganizationCustomPages />
    </IfGlobalOrg>
  )
}
